import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const lifecycleSteps = [
  {
    step: '01',
    icon: 'ri-lightbulb-line',
    titleKey: 'productsLifecycle.steps.0.title',
    descKey: 'productsLifecycle.steps.0.desc',
    color: '#FF2D78',
    durationKey: 'productsLifecycle.steps.0.duration',
  },
  {
    step: '02',
    icon: 'ri-settings-4-line',
    titleKey: 'productsLifecycle.steps.1.title',
    descKey: 'productsLifecycle.steps.1.desc',
    color: '#FF2D78',
    durationKey: 'productsLifecycle.steps.1.duration',
  },
  {
    step: '03',
    icon: 'ri-code-s-slash-line',
    titleKey: 'productsLifecycle.steps.2.title',
    descKey: 'productsLifecycle.steps.2.desc',
    color: '#FF2D78',
    durationKey: 'productsLifecycle.steps.2.duration',
  },
  {
    step: '04',
    icon: 'ri-shield-check-line',
    titleKey: 'productsLifecycle.steps.3.title',
    descKey: 'productsLifecycle.steps.3.desc',
    color: '#FF2D78',
    durationKey: 'productsLifecycle.steps.3.duration',
  },
  {
    step: '05',
    icon: 'ri-rocket-line',
    titleKey: 'productsLifecycle.steps.4.title',
    descKey: 'productsLifecycle.steps.4.desc',
    color: '#FF2D78',
    durationKey: 'productsLifecycle.steps.4.duration',
  },
  {
    step: '06',
    icon: 'ri-24-hours-line',
    titleKey: 'productsLifecycle.steps.5.title',
    descKey: 'productsLifecycle.steps.5.desc',
    color: '#FF2D78',
    durationKey: 'productsLifecycle.steps.5.duration',
  },
];

const subProducts = [
  { icon: 'ri-exchange-dollar-line', labelKey: 'nav.products.cex', path: '/products/cex' },
  { icon: 'ri-building-2-line', labelKey: 'nav.products.rwa', path: '/products/rwa' },
  { icon: 'ri-wallet-3-line', labelKey: 'nav.products.wallet', path: '/products/wallet' },
  { icon: 'ri-stack-line', labelKey: 'nav.products.staking', path: '/products/defi' },
  { icon: 'ri-cpu-line', labelKey: 'nav.products.mining', path: '/products/mining' },
];

export default function ProductsLifecycleSection() {
  const { t } = useTranslation();

  return (
    <>
      {/* Sub-products quick entry */}
      <section className="bg-pink-50/50 py-16 border-y border-primary/10">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div>
              <h2 className="text-2xl font-heading font-bold text-gray-900 mb-2">{t('productsLifecycle.quick.title')}</h2>
              <p className="text-gray-500 text-sm">{t('productsLifecycle.quick.subtitle')}</p>
            </div>
            <div className="flex flex-wrap gap-3">
              {subProducts.map((item, i) => (
                <Link
                  key={i}
                  to={item.path}
                  className="flex items-center gap-2 px-5 py-3 bg-white border border-primary/20 hover:border-primary/50 rounded-xl text-gray-600 hover:text-primary text-sm transition-all duration-200 cursor-pointer whitespace-nowrap group"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${item.icon} text-primary text-sm`} />
                  </div>
                  {t(item.labelKey)}
                  <div className="w-4 h-4 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <i className="ri-arrow-right-up-line text-xs text-primary" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Full Lifecycle */}
      <section className="bg-white py-24">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 mb-4 px-3 py-1.5 bg-primary/10 border border-primary/20 rounded-full">
              <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
              <span className="text-primary text-xs font-medium uppercase tracking-widest">{t('productsLifecycle.tag')}</span>
            </div>
            <h2 className="text-4xl font-heading font-bold text-gray-900 mb-4">{t('productsLifecycle.title')}</h2>
            <p className="text-gray-500 text-base max-w-xl mx-auto">
              {t('productsLifecycle.desc')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {lifecycleSteps.map((step, i) => (
              <div
                key={i}
                className="relative bg-white border border-primary/15 rounded-2xl p-6 overflow-hidden group hover:border-primary/35 transition-all duration-300"
              >
                <div className="absolute top-0 right-0 text-7xl font-heading font-black opacity-[0.04] leading-none pt-2 pr-3 text-primary">
                  {step.step}
                </div>
                <div className="w-10 h-10 flex items-center justify-center rounded-xl mb-4 bg-primary/10 border border-primary/20">
                  <i className={`${step.icon} text-primary text-lg`} />
                </div>
                <div className="text-xs font-mono mb-2 text-primary">
                  {t('productsLifecycle.stepPrefix')} {step.step} · {t(step.durationKey)}
                </div>
                <h3 className="text-gray-900 font-semibold text-base mb-2">{t(step.titleKey)}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{t(step.descKey)}</p>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-16 text-center">
            <div className="inline-flex flex-col sm:flex-row items-center gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary text-white font-semibold text-sm rounded-xl hover:bg-primary/90 transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-customer-service-line" />
                {t('productsLifecycle.cta.start')}
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-transparent border border-primary/25 text-gray-600 hover:text-primary hover:border-primary/50 font-medium text-sm rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                {t('productsLifecycle.cta.learn')}
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line" />
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
